/**
 * Cut / Copy / Paste for a custom context menu.
 *
 * A field that offers its own menu REPLACES the browser's, so it has to bring
 * these back — losing copy and paste to gain one action is a bad trade, and it
 * is why "To Variable" was opt-in and confined to the URL field until now
 * (owner, 22.09.2026).
 *
 * Paste is disabled when the clipboard is empty — but NEVER at the price of a
 * permission prompt. The first version called `navigator.clipboard.readText()`
 * directly, and in the browser that pops "localhost:3000 wants to see text and
 * images copied to the clipboard" the moment you right-click a field. Nobody
 * asked for that dialog, and no right-click menu is worth it (owner,
 * 22.09.2026).
 *
 * So the clipboard is only read when permission is ALREADY granted, which is
 * asked via `navigator.permissions` — a query that never prompts. Everywhere
 * else (prompt pending, denied, unsupported, non-secure origin) Paste is simply
 * left ENABLED. A disabled Paste that would have worked is a dead end with no
 * explanation; an enabled one that turns out to be empty does nothing, which is
 * what every native menu does anyway.
 *
 * In the packaged desktop app the permission is granted to the webview, so the
 * greying-out still works where it matters.
 */

/**
 * Can we paste right now?
 *
 * Never prompts. Returns false ONLY when the clipboard is readable without
 * asking and turns out to be empty.
 *
 * @returns {Promise<boolean>}
 */
export async function clipboardHasText() {
  try {
    if (typeof navigator === 'undefined' || !navigator.clipboard?.readText) return true;

    // `permissions.query` is itself optional and throws on engines that do not
    // know the `clipboard-read` name (Firefox), so a failure here means "do not
    // look" rather than "look anyway".
    if (!navigator.permissions?.query) return true;
    const status = await navigator.permissions.query({ name: 'clipboard-read' });
    if (status.state !== 'granted') return true;

    const text = await navigator.clipboard.readText();
    return typeof text === 'string' && text.length > 0;
  } catch {
    // Unsupported, refused, or unavailable — assume there is something.
    return true;
  }
}

/**
 * The three clipboard entries for a ContextMenu.
 *
 * `document.execCommand` rather than the async Clipboard API on purpose: it acts
 * on the CURRENT selection inside the focused editable and preserves the undo
 * stack, which `navigator.clipboard.writeText` plus a manual splice does not.
 * It is deprecated and still the only thing that does this correctly in a
 * contenteditable.
 *
 * @param {{ hasSelection: boolean, canPaste: boolean, onDone?: () => void, t: Function }} o
 */
export function clipboardMenuItems({ hasSelection, canPaste, onDone, t }) {
  const run = (command) => () => {
    try {
      document.execCommand(command);
    } catch {
      // A refused command must not take the menu down with it.
    }
    onDone?.();
  };

  return [
    { label: t('common.cut', { defaultValue: 'Cut' }), onClick: run('cut'), disabled: !hasSelection },
    { label: t('common.copy', { defaultValue: 'Copy' }), onClick: run('copy'), disabled: !hasSelection },
    { label: t('common.paste', { defaultValue: 'Paste' }), onClick: run('paste'), disabled: !canPaste },
  ];
}
